import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { projectsApi } from "@/api/projects.ts";
import { Alert, Button, Card, Field, Input, PageTitle } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { StateBadge } from "./StateBadge.tsx";

export function DockerBanner() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["system", "docker"], queryFn: projectsApi.dockerHealth, staleTime: 30_000 });
  if (!q.data) return null;
  if (!q.data.ok) return <Alert tone="danger">{t("projects.docker.down", { error: q.data.error })}</Alert>;
  if (!q.data.baseImagePresent) return <Alert tone="warn">{t("projects.docker.noImage", { image: q.data.baseImage })}</Alert>;
  return null;
}

export function ProjectsPage() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list });
  const [creating, setCreating] = useState(false);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <PageTitle title={t("projects.title")} />
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> {t("projects.new")}
        </Button>
      </div>
      <DockerBanner />
      {creating && <CreateProjectCard onClose={() => setCreating(false)} />}
      {q.data?.length === 0 && !creating && <Card className="text-sm text-fg-muted">{t("projects.empty")}</Card>}
      <ul className="grid gap-3 sm:grid-cols-2">
        {(q.data ?? []).map((p) => (
          <li key={p.id}>
            <Link to="/projects/$projectId" params={{ projectId: p.id }} className="block rounded-lg border border-border bg-surface p-4 transition-colors hover:border-accent/60">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{p.name}</div>
                  <div dir="ltr" className="truncate text-start font-mono text-xs text-fg-muted">{p.slug}</div>
                </div>
                <StateBadge state={p.state} />
              </div>
              {p.description && <p className="mt-2 line-clamp-2 text-sm text-fg-muted">{p.description}</p>}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CreateProjectCard({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const m = useMutation({
    mutationFn: async () => {
      const p = await projectsApi.create({ name: form.name, description: form.description, limits: {}, ...(form.slug ? { slug: form.slug } : {}) });
      await projectsApi.start(p.id).catch(() => undefined); // new projects start right away
      return p;
    },
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
      void nav({ to: "/projects/$projectId", params: { projectId: p.id } });
    },
  });
  return (
    <Card>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {m.error && <Alert tone="danger">{toMsg(m.error)}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("projects.name")}>
            <Input required autoFocus maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label={t("projects.slug")} hint={t("projects.slugHint")}>
            <Input dir="ltr" className="font-mono" pattern="[a-z0-9](?:[a-z0-9\-]{1,38}[a-z0-9])?" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} />
          </Field>
        </div>
        <Field label={t("projects.description")}>
          <Input maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" loading={m.isPending}>{t("projects.create")}</Button>
          <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        </div>
      </form>
    </Card>
  );
}
